import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { getAllData } from "../../../../utils/helpers/fetching";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { withApollo } from "react-apollo";
import { PaginationContext } from "../../../../components/shared/pagination/PaginationContext";
import { AssignGroupContext } from "./context";
import { GroupTableMeta } from "./GroupMeta";
import TopBar from "./TopBar";
import { Formik } from "formik";
import { groupValidationSchema } from "./validationSchema";
import { OwcPagination } from "@one/react";
import BackdropPortal from "../../../../components/shared/BackdropPortal";
import GroupDialog from "./GroupDialog";
import {
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT_MODEL,
  LIST_BOOKING_ACCESS_CONFIG_BY_EQUIP_MODEL,
  LIST_BOOKING_GROUP_CONFIGURATION,
  LIST_SITES,
} from "../../../../gql/bookingapi";
import {
  getSiteDetails,
  getFloorsByBuildingIdAndFloorCode,
  getRoomsByFloorIdAndRoomCode,
  getBuildingsBySiteIdAndBuildingNumber,
} from "../../../equipment-list/LoadEquipmentService";

import {
  CREATE_BOOKING_ACCESS_CONFIGURATION_MODEL,
  UPDATE_BOOKING_ACCESS_CONFIGURATION_MODEL,
  DELETE_BOOKING_ACCESS_EQUIPMENT_GROUP_OR_MODAL_LOCATION
} from "../../../../gql/bookingapi/mutations";
import CustomList from "../../../../components/shared/List";
import { GroupPaginationStyled } from "./GroupMainPageStyle";
import {
  loadBookingInstruments as loadBookingInstrumentsAction,
  loadUnfilteredBookingInstruments as loadUnfilteredBookingInstrumentsAction,
} from "../../../booking-instruments/redux/actions";
import * as adminActions from "../../../booking-admin/redux/actions";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { DURATION } from "../../../../constants";
import { BookingAccessControlContext } from "../BookingAccessControlContext";
import DeleteModalDialog from "./DeleteModalDialog";
import { filterGroups } from "../../../../utils/filterGroups";

const GroupContent = ({ client, user, siteName, loadBookingInstruments, loadUnfilteredBookingInstruments }) => {
  // PaginationContext and AssignGroupContext
  const { dispatchAction, fetching, refreshData,nextData } =
    useContext(PaginationContext);
  const { dialogOpen,setDialogOpen,dialogDeleteOpen,setDialogDeleteOpen, isEditMode, lastEditGroup,isDeleteMode, setIsDeleteMode } =
    useContext(AssignGroupContext);
  const { refreshToken } = useContext(BookingAccessControlContext);
  // Use the useDispatch hook directly
  const dispatch = useDispatch();
  const [disableModalAccordion, setDisableModalAccordion] = useState(true);
  // Local state management
  const [mainList, setMainList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [searchGroup, setSearchGroup] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [showPerPage, setShowPerPage] = useState(5);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [locationDataLoaded, setLocationDataLoaded] = useState(false);
  const [lastEditGroupId, setLastEditGroupId] = useState(null);
  const prevSearchGroupRef = useRef("");
  const [filteredList, setFilteredList] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const { adminBuildingsList, adminFloorsList, adminRoomsList } = useSelector(
    (state) => state.bookingAdmin.locationData
  );

  // Main list data loading
  const getMainList = async () => {
    let variables = {
      modelName: "BOOKINGACCESSCONFIG",
    };
    const { items } = await getAllData({
      client,
      query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT_MODEL,
      variables,
      fetchPolicy: "network-only",
      dataPath: ["data", "listBookingAccessConfigurationByEquipmentModel"],
      drillData: false,
    });

    (items || []).sort((a, b) => (a.equipmentModelName || "").localeCompare(b.equipmentModelName || ""));

    let newItems = items
      .map((item) => {
        const userList = (item.groups?.items[0]?.users || []).map((x) =>
          x?.givenName && x?.familyName ? x.givenName + " " + x.familyName : x.email
        );
        item.users = userList?.join(", ") || "";
        item.cidmGroup = item.groups?.items[0]?.cidmGroup;
        return item;
      })
      .filter((item) => item.equipmentModel !== null)
      .sort((a, b) => {
        const valA = a?.equipmentModelId?.toString().toLowerCase() ?? "";
        const valB = b?.equipmentModelId?.toString().toLowerCase() ?? "";
        return valA.localeCompare(valB);
      });
    setMainList(newItems);
  };

  const getGroupList = async () => {
    const { items } = await getAllData({
      client,
      query: LIST_BOOKING_GROUP_CONFIGURATION,
      fetchPolicy: "network-only",
      dataPath: ["data", "listBookingGroupConfigurations"],
      drillData: true,
      variables: {
        limit: 1000,
      },
    });
    const list = items.filter((item) => item.groupName !== "");
    setGroupList(list);
  };

  // Form submission handler
  const submit = async (data) => {
    if (!isEditMode) {
      if (selectedEquipment === null) {
        snackbarService.show({
          message: "Invalid equipment.",
          type: "error",
          duration: DURATION,
        });
        return;
      }
      let variablesValidation = {};
      const { items } = await getAllData({
        client,
        query: LIST_BOOKING_ACCESS_CONFIG_BY_EQUIP_MODEL,
        variables: variablesValidation,
        fetchPolicy: "network-only",
        dataPath: ["data", "listBookingAccessConfigurationByEquipment"],
        drillData: true,
      });
      if (items?.length > 0) {
        snackbarService.show({
          message: `Equipment is already assigned to a group.`,
          type: "error",
          duration: DURATION,
        });
        return;
      }
    }
    const locationKey = [data.site, data.building, data.floor, data.room].filter(Boolean).join(";");
    let variables = {
      equipmentModelId: isEditMode
        ? lastEditGroup?.equipment?.equipmentModel || lastEditGroup?.equipmentModelId
        : selectedEquipment?.equipmentModel,

      equipmentModelName: isEditMode
        ? lastEditGroup?.equipment?.equipmentModel || lastEditGroup?.equipmentModelId
        : selectedEquipment?.equipmentModel,

      modelName: "BOOKINGACCESSCONFIG",
      groupName: data.group,

      location: {
        site: data.site || "",
        building: data.building || "",
        floor: data.floor || "",
        room: data.room || "",
      },
      locationKey,
    };
    if (isEditMode) {
      variables = {
        ...variables,
        id: lastEditGroup?.id,
      };
    }
    try {
      await client.mutate({
        mutation: isEditMode ? UPDATE_BOOKING_ACCESS_CONFIGURATION_MODEL : CREATE_BOOKING_ACCESS_CONFIGURATION_MODEL,
        variables,
        fetchPolicy: "no-cache",
      });
      dispatchAction({
        type: "clearAll",
      });
      setDialogOpen(false);
      snackbarService.show({
        message: isEditMode ? `Assign group updated successfully! ` : `Assign group created successfully! `,
        type: "success",
        duration: DURATION,
      });
    } catch (error) {
      setDialogOpen(true);
      const message =
        error.graphQLErrors?.[0]?.message ||
        (isEditMode ? "Assign group update failed." : "Assign group creation failed.");
      snackbarService.show({
        message,
        type: "error",
        duration: DURATION,
      });
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      getGroupList();
      // Load all location data when dialog opens
      getLocationData();

      // If editing an existing item, load specific location data
      if (isEditMode && lastEditGroup) {
        // Reset location data when switching to a different group
        if (lastEditGroupId !== lastEditGroup.id) {
          dispatch(adminActions.loadAdminBuildings([]));
          dispatch(adminActions.loadAdminFloors([]));
          dispatch(adminActions.loadAdminRooms([]));
          setLastEditGroupId(lastEditGroup.id);
        }

        setLocationDataLoaded(false);
        const site = lastEditGroup.location?.site || "";
        if (site) {
          getLocationBuildings(site).then((buildings) => {
            const building = lastEditGroup.location?.building;
            const matchingBuilding = buildings.find((b) => b.name === building);

            if (matchingBuilding) {
              getLocationFloors(matchingBuilding.id).then((floors) => {
                const floor = lastEditGroup.location?.floor;
                const matchingFloor = floors.find((f) => f.name === floor);

                if (matchingFloor) {
                  getLocationRooms(matchingFloor.id).then(() => {
                    setLocationDataLoaded(true);
                  });
                } else {
                  setLocationDataLoaded(true);
                }
              });
            } else {
              setLocationDataLoaded(true);
            }
          });
        } else {
          setLocationDataLoaded(true);
        }
      } else {
        setLocationDataLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, lastEditGroup?.id]);

   // Load main data on filters change
  useEffect(() => {
    getMainList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData, refreshToken, nextData,client]);

  const filteredItems = useMemo(() => {
    return filterGroups(mainList, searchGroup, [
      item => item.groupName,
      item => item.equipmentModelName,
      item => item.locationKey,
    ]);
  }, [mainList, searchGroup]);
  
  // Load main data on filters change
  useEffect(() => {
    if (searchGroup !== prevSearchGroupRef.current) {
      setPageNo(1);
      prevSearchGroupRef.current = searchGroup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGroup]);

  useEffect(() => {
    setTotalCount(filteredItems.length);

    const lowIndex = (pageNo - 1) * showPerPage;
    const highIndex = lowIndex + showPerPage;

    const paginationData = filteredItems.slice(lowIndex, highIndex);
    setFilteredList(paginationData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, pageNo, showPerPage]);

  // Handle site change - fetch buildings and update Redux
  const handleSiteChange = (siteName) => {
    if (siteName) {
      // Use our new location query function
      getLocationBuildings(siteName);

      // Reset dependent fields in Redux
      dispatch(adminActions.loadAdminFloors([]));
      dispatch(adminActions.loadAdminRooms([]));
    }
  };

  // Handle building change - fetch floors for the selected building
  const handleBuildingChange = (buildingId) => {
    if (buildingId) {
      getLocationFloors(buildingId);

      // Reset rooms in Redux
      dispatch(adminActions.loadAdminRooms([]));
    }
  };

  // Handle floor change - fetch rooms for the selected floor
  const handleFloorChange = (floorId) => {
    if (floorId) {
      getLocationRooms(floorId);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogDeleteOpen(false);
    // Reset state when closing dialog
    setLocationDataLoaded(false);
    setLastEditGroupId(null);
    dispatch(adminActions.loadAdminBuildings([]));
    dispatch(adminActions.loadAdminFloors([]));
    dispatch(adminActions.loadAdminRooms([]));
  };

  const getLocationData = async () => {
    try {
      const sites = await getLocationSites();
      dispatch(adminActions.loadSitesList(sites));
    } finally {
    }
  };

  const getLocationSites = async () => {
    try {
      const { items } = await getAllData({
        client,
        query: LIST_SITES,
        fetchPolicy: "network-only",
        dataPath: ["data", "listSites"],
        drillData: true,
        variables: {
          limit: 1000,
        },
      });

      // Ensure each site has a unique key for React rendering
      const sitesWithUniqueKeys = items.map((site, index) => ({
        ...site,
        uniqueKey: `${site.siteName}-${index}`,
      }));

      return sitesWithUniqueKeys || [];
    } catch (error) {
      console.error("Error fetching sites:", error);
      return [];
    }
  };

  const getLocationBuildings = async (siteNameParam) => {
    try {
      // Get the site ID first
      const siteResult = await getSiteDetails(client, siteNameParam || siteName);

      if (siteResult?.siteId) {
        // Store the site ID in Redux
        dispatch(adminActions.setAdminSiteId(siteResult.siteId));

        // Get buildings for this site
        const buildingsResult = await getBuildingsBySiteIdAndBuildingNumber(client, siteResult.siteId);

        // Transform data to match expected format
        const formattedBuildings = buildingsResult.items.map((building, index) => ({
          id: building.id,
          buildingId: building.buildingId,
          name: building.buildingNumber || building.id,
          uniqueKey: `${building.id}-${index}`,
        }));

        // Update Redux with buildings data
        dispatch(adminActions.loadAdminBuildings(formattedBuildings));

        return formattedBuildings;
      }
      return [];
    } catch (error) {
      console.error("Error getting buildings:", error);
      return [];
    }
  };

  const getLocationFloors = async (buildingId) => {
    try {
      if (!buildingId) return [];
      const result = await getFloorsByBuildingIdAndFloorCode(client, parseInt(buildingId));
      // Transform data to match expected format
      const formattedFloors = result.items.map((floor, index) => ({
        id: floor.id,
        floorId: floor.floorId,
        name: floor.floorCode || floor.id,
        uniqueKey: `${floor.id}-${index}`,
      }));

      // Update Redux with floors data
      dispatch(adminActions.loadAdminFloors(formattedFloors));

      return formattedFloors;
    } catch (error) {
      console.error("Error getting floors:", error);
      return [];
    }
  };

  const getInitialValues = () => {
    if (isEditMode && lastEditGroup) {
      const initialValues = {
        model: (lastEditGroup?.equipmentModelId || lastEditGroup?.equipment?.equipmentModel) ?? "",
        group: lastEditGroup?.groupName ?? "",
        site: lastEditGroup?.location?.site ?? "",
        building: lastEditGroup?.location?.building ?? "",
        floor: lastEditGroup?.location?.floor ?? "",
        room: lastEditGroup?.location?.room ?? "",
      };

      return initialValues;
    }
    return {
      model: "",
      group: "",
      site: "",
      building: "",
      floor: "",
      room: "",
    };
  };

  const getLocationRooms = async (floorId) => {
    try {
      if (!floorId) return [];

      const result = await getRoomsByFloorIdAndRoomCode(client, parseInt(floorId));

      // Transform data to match expected format
      const formattedRooms = result.items.map((room, index) => ({
        id: room.id,
        roomId: room.roomId,
        name: room.roomCode || room.id,
        uniqueKey: `${room.id}-${index}`,
      }));

      // Update Redux with rooms data
      dispatch(adminActions.loadAdminRooms(formattedRooms));

      return formattedRooms;
    } catch (error) {
      console.error("Error getting rooms:", error);
      return [];
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formInitialValues = useMemo(
    () => ({ ...getInitialValues() }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastEditGroup?.id, isEditMode]
  );

  const shouldRenderForm = dialogOpen && (!isEditMode || !!lastEditGroup) && (!isEditMode || locationDataLoaded);

  const showLoadingSpinner = dialogOpen && isEditMode && !locationDataLoaded;

  const deleteModalAccordion = async () => {
      setDisableModalAccordion(true);
      let row={
         id: lastEditGroup?.id
      }
      try{
        await client.mutate({
          mutation:DELETE_BOOKING_ACCESS_EQUIPMENT_GROUP_OR_MODAL_LOCATION,
          variables:row,
          fetchPolicy:"no-cache",
        });
        dispatchAction({
          type:"clearAll"
        });
        snackbarService.show({
           message: `Equipment modal location deleted successfully!`,
           type: "success",
           duration: DURATION,
        })
      }catch(error){
        snackbarService.show({
           message: `Failed to delete equipment modal location!`,
           type: "error",
           duration: DURATION,
        })
      }finally{
        setIsDeleteMode(false);
        setDialogDeleteOpen(false);
        setDisableModalAccordion(false);
      }
    };
    
  return (
    <>
      <Formik
        validationSchema={groupValidationSchema}
        initialValues={formInitialValues}
        enableReinitialize={true}
        isInitialValid={true}
        validateOnMount={false}
      >
        <>
          <BackdropPortal show={showLoadingSpinner} />
          {shouldRenderForm && (
            <GroupDialog
              open={dialogOpen}
              onCancel={closeDialog}
              isEditMode={isEditMode}
              submit={submit}
              groupList={groupList}
              setSelectedEquipment={setSelectedEquipment}
              onSiteChange={handleSiteChange}
              onBuildingChange={handleBuildingChange}
              onFloorChange={handleFloorChange}
            />
          )}
          {isDeleteMode && (
            <DeleteModalDialog
              open={dialogDeleteOpen}
              onCancel={closeDialog}
              deleteModalAccordion={deleteModalAccordion}
              disableModalAccordion={disableModalAccordion}
              setDisableModalAccordion={setDisableModalAccordion}
            />
          )}
          <TopBar setSearchGroup={setSearchGroup} />
          <CustomList
            meta={GroupTableMeta}
            data={filteredList}
            fieldArray={[]}
            isReverseOrder={true}
            orderBy={"name"}
            loading={fetching}
          />
        </>
      </Formik>

      <GroupPaginationStyled>
        <OwcPagination
          onPageChange={(e) => {
            setShowPerPage(e.detail.rowsPerPage);
            setPageNo(e.detail.page);
          }}
          options={[5, 10, 20, 50]}
          rowsPerPage={showPerPage}
          total={Math.ceil(totalCount / showPerPage)}
          page={pageNo}
        >
          <span slot="rows-per-page">Rows per page</span>
          <div slot="indicator">
            {pageNo} of {Math.ceil(totalCount / showPerPage)}
          </div>
        </OwcPagination>
      </GroupPaginationStyled>
    </>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  siteName: state.user.site,
});

export default compose(
  connect(mapStateToProps, {
    loadBookingInstruments: loadBookingInstrumentsAction,
    loadUnfilteredBookingInstruments: loadUnfilteredBookingInstrumentsAction,
  }),
  withApollo
)(GroupContent);
